import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { SWRConfig } from "swr";
import { clearSessionCache } from "@/lib/sessionCache";
import { localStorageProvider, swrDefaultConfig, clearSwrPersistedCache } from "@/lib/swrConfig";
import { Toaster } from "@/components/ui/sonner";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { YearProvider } from "@/contexts/YearContext";

// Pages
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import InterviewsPage from "@/pages/InterviewsPage";
import OffersPage from "@/pages/OffersPage";
import FunnelRipresaPage from "@/pages/FunnelRipresaPage";
import OrdiniPage from "@/pages/OrdiniPage";
import CronoOrdiniPage from "@/pages/CronoOrdiniPage";
import CommessePosePage from "@/pages/CommessePosePage";
import VerificaCompletamentiPage from "@/pages/VerificaCompletamentiPage";
import AttenzionatiPage from "@/pages/AttenzionatiPage";
import PaymentsPage from "@/pages/PaymentsPage";
import ClientOfferPage from "@/pages/ClientOfferPage";
import ClientAppuntamentoPage from "@/pages/ClientAppuntamentoPage";
import ClientRilievoPage from "@/pages/ClientRilievoPage";
import SettingsPage from "@/pages/SettingsPage";
import UsersPage from "@/pages/UsersPage";
import PoseMagazzinoPage from "@/pages/PoseMagazzinoPage";
import CommessaDetailPage from "@/pages/CommessaDetailPage";
import RilievoMisurePage from "@/pages/RilievoMisurePage";
import AppuntamentiPage from "@/pages/AppuntamentiPage";
import TemplateEmailPage from "@/pages/TemplateEmailPage";
import CalendariRapidiPage from "@/pages/CalendariRapidiPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Pulisce la cache client-side per non lasciare dati accessibili all'utente successivo
      clearSessionCache("dashboard_state_");
      clearSessionCache("ordini_");
      clearSessionCache("payments_");
      clearSessionCache("interviews_");
      clearSessionCache("commesse_pose_");
      clearSessionCache("offers_");
      clearSwrPersistedCache();
      setUser(null);
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  return { user, setUser, loading, checkAuth, logout };
};

// Protected route wrapper
const ProtectedRoute = ({ children, user, loading }) => {
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function AppContent() {
  const { user, setUser, loading, checkAuth, logout } = useAuth();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/login" element={
          loading ? (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
              <div className="spinner" />
            </div>
          ) : user ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <LoginPage onLogin={handleLogin} />
          )
        } />
        
        <Route path="/dashboard" element={
          <ProtectedRoute user={user} loading={loading}>
            <Dashboard user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/interviews" element={
          <ProtectedRoute user={user} loading={loading}>
            <InterviewsPage user={user} logout={logout} />
          </ProtectedRoute>
        } />

        <Route path="/appuntamenti" element={
          <ProtectedRoute user={user} loading={loading}>
            <AppuntamentiPage user={user} logout={logout} />
          </ProtectedRoute>
        } />

        <Route path="/template-email" element={
          <ProtectedRoute user={user} loading={loading}>
            <TemplateEmailPage user={user} logout={logout} />
          </ProtectedRoute>
        } />

        <Route path="/calendari-rapidi" element={
          <ProtectedRoute user={user} loading={loading}>
            <CalendariRapidiPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/offers" element={
          <ProtectedRoute user={user} loading={loading}>
            <OffersPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/funnel-ripresa" element={
          <ProtectedRoute user={user} loading={loading}>
            <FunnelRipresaPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/rilievo-misure" element={
          <ProtectedRoute user={user} loading={loading}>
            <RilievoMisurePage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/ordini" element={
          <ProtectedRoute user={user} loading={loading}>
            <OrdiniPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/crono-ordini" element={
          <ProtectedRoute user={user} loading={loading}>
            <CronoOrdiniPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/payments" element={
          <ProtectedRoute user={user} loading={loading}>
            <PaymentsPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/commesse-pose" element={
          <ProtectedRoute user={user} loading={loading}>
            <CommessePosePage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/verifica-completamenti" element={
          <ProtectedRoute user={user} loading={loading}>
            <VerificaCompletamentiPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/attenzionati" element={
          <ProtectedRoute user={user} loading={loading}>
            <AttenzionatiPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/commesse-pose/:id" element={
          <ProtectedRoute user={user} loading={loading}>
            <CommessaDetailPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/pose-magazzino" element={
          <ProtectedRoute user={user} loading={loading}>
            <PoseMagazzinoPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/pose-magazzino/:id" element={
          <ProtectedRoute user={user} loading={loading}>
            <CommessaDetailPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/settings" element={
          <ProtectedRoute user={user} loading={loading}>
            <SettingsPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        <Route path="/users" element={
          <ProtectedRoute user={user} loading={loading}>
            <UsersPage user={user} logout={logout} />
          </ProtectedRoute>
        } />
        
        {/* Client portal - no auth required */}
        <Route path="/offer/:magicLink" element={<ClientOfferPage />} />
        <Route path="/appuntamento/:magicLink" element={<ClientAppuntamentoPage />} />
        <Route path="/rilievo/:magicLink" element={<ClientRilievoPage />} />
        
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SWRConfig value={{ provider: localStorageProvider, ...swrDefaultConfig }}>
        <YearProvider>
          <SidebarProvider>
            <AppContent />
          </SidebarProvider>
        </YearProvider>
      </SWRConfig>
    </BrowserRouter>
  );
}

export default App;
